import { PortableTextReactComponents } from '@portabletext/react'
import ReactMarkdown from 'react-markdown'
import SyntaxHighlighter from 'react-syntax-highlighter'
import { BlockMath } from 'react-katex'
import 'katex/dist/katex.min.css'
import Figure from './Figure'
import { EmbeddedVideo } from './EmbeddedVideo'
import CodeEditorWidget from './CodeEditorWidget'
import DatasetPreview from './DatasetPreview'
import ExpectedOutputWidget from './ExpectedOutputWidget'
import AlgorithmConstraintsWidget from './AlgorithmConstraintsWidget'
import AlgorithmTestsWidget from './AlgorithmTestsWidget'
import QuestionDetails from './QuestionDetails'

const components: Partial<PortableTextReactComponents> = {
  block: {
    normal: ({ children }) => (
      <p className="my-6 text-base/8 text-neutral-800">{children}</p>
    ),
    h1: ({ children }) => (
      <h1 className="mb-8 mt-16 text-4xl font-bold">{children}</h1>
    ),
    h2: ({ children }) => (
      <h2 className="mb-8 mt-12 text-3xl font-bold">{children}</h2>
    ),
    h3: ({ children }) => <h3 className="mb-6 mt-12 text-2xl">{children}</h3>,
    h4: ({ children }) => <h4 className="mb-6 mt-10 text-lg">{children}</h4>,
  },
  marks: {
    color: ({ value, children }) => (
      <span style={{ color: value.hex }}>{children}</span>
    ),
    link: ({ value, children }) => {
      const target =
        (value?.href || '').startsWith('http') &&
        !value?.href.includes('stratascratch.com')
          ? '_blank'
          : undefined
      const isExternal = target === '_blank'
      const doFollow = !!value?.dofollow
      const rel = isExternal && !doFollow ? 'noindex nofollow' : ''
      return (
        <a
          className="text-(--color-link) break-words"
          href={value?.href}
          target={target}
          rel={rel}
        >
          {children}
        </a>
      )
    },
    code: ({ children }) => (
      <code className="bg-(--bg-color-secondary) border-(--color-border) rounded-sm border p-0.5 text-sm">
        {children}
      </code>
    ),
  },
  list: {
    bullet: ({ children }) => (
      <ul className="my-8 list-disc ps-6 text-base/8 text-neutral-800 marker:text-neutral-500 [&_ol]:my-2 [&_ul]:my-2">
        {children}
      </ul>
    ),
    number: ({ children }) => (
      <ol className="marker-nested my-8 ps-6 text-base/8 text-neutral-800 marker:text-neutral-600 [&_ol]:my-2 [&_ul]:my-2">
        {children}
      </ol>
    ),
  },
  listItem: ({ children }) => <li className="my-2 ps-2">{children}</li>,
  types: {
    authorReference: ({ value }) => <span>{value.author.name}</span>,
    mainImage: Figure,
    youtube: EmbeddedVideo,
    markdownType: ({ value }) => {
      const { body } = value
      return <ReactMarkdown>{body}</ReactMarkdown>
    },
    datatableBlock: ({
      value,
    }: {
      value: { rows: { _key: string; cells: string[] }[] }
    }) => (
      <table>
        <tbody>
          {value.rows.map((row) => (
            <tr key={row._key}>
              {row.cells.map((cell) => (
                <td key={cell}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    ),
    mathFormula: ({ value }) => {
      const { body } = value
      return <BlockMath math={body} />
    },
    codeDisplay: ({ value }) => (
      <SyntaxHighlighter
        language={value.languageDisplay.language}
        customStyle={{
          padding: '1rem',
          background: 'var(--bg-color-dim)',
          borderRadius: '0.5rem',
        }}
      >
        {value.languageDisplay.code}
      </SyntaxHighlighter>
    ),
    codeEditor: CodeEditorWidget,
    datasetPreview: DatasetPreview,
    expectedOutput: ExpectedOutputWidget,
    algorithmConstraints: AlgorithmConstraintsWidget,
    algorithmTests: AlgorithmTestsWidget,
    questionDetails: QuestionDetails,
  },
}

export default components
